import React, { useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const StyledCarouselContainer = styled.div`
  width: ${props => props.width || '80%'};
  max-width: ${props => props.$maxWidth || 'none'};
  margin: 0 auto;
  padding: ${props => props.padding || '20px'};
  .slick-slide {
    text-align: center;
  }

  .slick-prev:before,
  .slick-next:before {
    color: #000;
  }

  @media (max-width: 768px) {
    width: 95%;
    padding: 10px;
  }
`;

const SlideContainer = styled.div`
  box-sizing: border-box;
  padding: 5px;
`

const SlideContent = styled.div`
  cursor: pointer;
  position: relative;
  width: 100%;
  padding-top: ${props => props.$aspectRatio || '56.25%'};
  background: #f0f0f0;
  border-radius: ${props => props.$borderRadius || '8px'};
  overflow: hidden;
`;

const SlideImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$objectFit || 'contain'};
`;

const SlideCaption = styled.p`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 10px;
  margin: 0;
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  max-width: 90%;
  max-height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.3);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 24px;
  transition: background-color 0.3s;
  z-index: 1001;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }

  ${props => props.$position === 'left' ? 'left: 10px;' : 'right: 10px;'}
`;

const PopupImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  touch-action: none;
`;

function CarouselContainer({ 
  images, 
  width, 
  $maxWidth,
  $aspectRatio, 
  padding,
  $borderRadius,
  $objectFit,
  sliderSettings = {}
}) {
  const [popupImage, setPopupImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const defaultSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const settings = { ...defaultSettings, ...sliderSettings };

  const handleSlideClick = (image) => {
    setPopupImage(image);
  };

  const closePopup = () => {
    setPopupImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex = (currentImageIndex + direction + images.length) % images.length;
    setCurrentImageIndex(newIndex);
    setPopupImage(images[newIndex]);
  };

  // Mobile touch handlers
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swiped left
        navigateImage(1);
      } else {
        // Swiped right
        navigateImage(-1);
      }
    }
  };

  return (
    <>
      <StyledCarouselContainer width={width} $maxWidth={$maxWidth} padding={padding}>
        <Slider {...settings} slidesToShow={sliderSettings.slidesToShow || 1}>
          {images.map((image, index) => (
            <SlideContainer key={index}>
              <SlideContent
                onClick={() => handleSlideClick(image)}
                $aspectRatio={$aspectRatio}
                $borderRadius={$borderRadius}
              >
                <SlideImage
                  src={image.src}
                  alt={`Slide ${index + 1}`}
                  $objectFit={$objectFit}
                />
              </SlideContent>
            </SlideContainer>
          ))}
        </Slider>
      </StyledCarouselContainer>

      {popupImage && (
        <PopupOverlay onClick={closePopup}>
          <PopupContent onClick={(e) => e.stopPropagation()}>
            <NavigationButton
              $position="left"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(-1);
              }}
            >
              ‹
            </NavigationButton>
            <PopupImage
              src={popupImage.src}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
            <NavigationButton
              $position="right"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(1);
              }}
            >
              ›
            </NavigationButton>
          </PopupContent>
        </PopupOverlay>
      )}
    </>
  );
}

export default CarouselContainer;